import * as THREE from 'three';
import { createGltfLoader } from './gltf.js';
import {
  SKIFF_HARDPOINTS,
  type Hardpoint,
  type MountTransform,
} from '@blackmarble/sim-core';
import { normaliseToRadius } from './bodyFactory.js';
import { upgradeHullMaterials } from './hullMaterial.js';

const PLAYER_SHIP_DISPLAY_KM = 5;
const loader = createGltfLoader();
const PLACEHOLDER_MAT = new THREE.MeshStandardMaterial({
  color: 0x1b1d20,
  roughness: 0.94,
  metalness: 0.08,
  envMapIntensity: 0,
});
const skiffHardpoints = new Map<string, Hardpoint>(
  SKIFF_HARDPOINTS.map((hardpoint) => [hardpoint.nodeName, hardpoint]),
);

export interface PlayerShipRenderable {
  root: THREE.Group;
  resolveHardpoint: (nodeName: string) => THREE.Object3D;
}

const normalisePlayerHull = (hull: THREE.Object3D): THREE.Object3D => {
  normaliseToRadius(hull, PLAYER_SHIP_DISPLAY_KM);
  hull.position.set(0, 0, 0);
  hull.rotation.set(0, 0, 0);
  // Wave 2b PBR: clearcoat the canopy glass (no-op on hulls without a named canopy).
  upgradeHullMaterials(hull);
  // Match the NPC hull IBL boost so the player ship reads as lit material too.
  // WAVE 2a: the player hull both casts and receives the sun shadow (self-shadowed
  // matte metal reads more funereal); it is the frustum centre under floating origin.
  hull.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.castShadow = true;
    o.receiveShadow = true;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    for (const mat of mats) {
      if ('envMapIntensity' in mat) {
        (mat as THREE.MeshStandardMaterial).envMapIntensity = 2.0;
        mat.needsUpdate = true;
      }
    }
  });
  return hull;
};

const mountBasis = (transform: MountTransform): THREE.Quaternion => {
  const z = new THREE.Vector3(
    transform.forward.x,
    transform.forward.y,
    transform.forward.z,
  ).normalize();
  const up = new THREE.Vector3(
    transform.up?.x ?? 0,
    transform.up?.y ?? 1,
    transform.up?.z ?? 0,
  ).normalize();
  const x = new THREE.Vector3().crossVectors(up, z).normalize();
  const y = new THREE.Vector3().crossVectors(z, x).normalize();
  return new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(x, y, z),
  );
};

export const buildHardpointFallbackNode = (
  nodeName: string,
  transform: MountTransform,
): THREE.Object3D => {
  const node = new THREE.Object3D();
  node.name = nodeName;
  node.position.set(
    transform.position.x,
    transform.position.y,
    transform.position.z,
  );
  node.quaternion.copy(mountBasis(transform));
  return node;
};

const skiffHardpoint = (nodeName: string): Hardpoint => {
  const hardpoint = skiffHardpoints.get(nodeName);
  if (!hardpoint) throw new Error(`Unknown skiff hardpoint: ${nodeName}`);
  return hardpoint;
};

export const resolveHardpointNode = (
  root: THREE.Object3D,
  nodeName: string,
): THREE.Object3D => {
  const existing = root.getObjectByName(nodeName);
  if (existing) return existing;
  const fallback = buildHardpointFallbackNode(nodeName, skiffHardpoint(nodeName).transform);
  root.add(fallback);
  return fallback;
};

const addPlaceholderPart = (
  parent: THREE.Group,
  geometry: THREE.BufferGeometry,
  position: [number, number, number],
  rotation: [number, number, number] = [0, 0, 0],
): void => {
  const mesh = new THREE.Mesh(geometry, PLACEHOLDER_MAT);
  mesh.position.set(...position);
  mesh.rotation.set(...rotation);
  parent.add(mesh);
};

const makePlaceholderHull = (): THREE.Object3D => {
  const hull = new THREE.Group();
  addPlaceholderPart(hull, new THREE.CylinderGeometry(0.9, 1.1, 4.6, 8), [0, 0, 0], [Math.PI / 2, 0, 0]);
  addPlaceholderPart(hull, new THREE.ConeGeometry(0.78, 1.8, 8), [0, 0, -3.1], [Math.PI / 2, 0, 0]);
  addPlaceholderPart(hull, new THREE.BoxGeometry(4.8, 0.18, 1.5), [0, -0.18, 0.15]);
  addPlaceholderPart(hull, new THREE.BoxGeometry(1.9, 0.22, 1.3), [0, 0.62, 0.55]);
  addPlaceholderPart(hull, new THREE.BoxGeometry(0.7, 0.95, 1.3), [0, 0.12, 1.95]);
  return normalisePlayerHull(hull);
};

const loadHull = async (shipId: string, baseUrl: string): Promise<THREE.Object3D> => {
  try {
    const gltf = await loader.loadAsync(`${baseUrl}${shipId}.glb`);
    return normalisePlayerHull(gltf.scene);
  } catch (err) {
    console.warn(
      `[assets] player ship GLB missing (${shipId}) — using matte placeholder hull`,
      err,
    );
    return makePlaceholderHull();
  }
};

export const loadPlayerShip = async (
  shipId = 'skiff',
  baseUrl?: string,
): Promise<PlayerShipRenderable> => {
  const resolvedBaseUrl = baseUrl ?? (await import('./assetDir.js')).assetDir('ships');
  const root = new THREE.Group();
  root.name = 'player-ship';
  root.add(await loadHull(shipId, resolvedBaseUrl));
  return {
    root,
    resolveHardpoint: (nodeName: string) => resolveHardpointNode(root, nodeName),
  };
};
